import React, { useEffect, useState } from 'react';
import SparkleEffect from './SparkleEffect';
import CountdownTimer from './CountdownTimer';
import { Flame } from 'lucide-react';

const LandingPage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const backgroundImage = "/images/background.jpg";

  useEffect(() => {
    // Preload image for better performance
    const img = new Image();
    img.onload = () => {
      setImageLoaded(true);
      // Start content fade in after image loads - faster for mobile
      setTimeout(() => setIsVisible(true), 150);
    };
    img.src = backgroundImage;

    // Prevent pull-to-refresh on Instagram/Threads in-app browser
    document.body.style.overscrollBehavior = 'none';

    // Optimize for touch devices
    document.body.style.touchAction = 'manipulation';

    return () => {
      document.body.style.overscrollBehavior = 'auto';
      document.body.style.touchAction = 'auto';
    };
  }, [backgroundImage]);

  const handleFanvueClick = () => {
    // Try to open in external browser for Instagram/Threads
    const url = 'https://www.fanvue.com/gentlemelt';
    window.location.href = url;
  };

  const handleTelegramClick = () => {
    // Try to open in external browser for Instagram/Threads
    const url = 'https://t.me/Gentlemelt';
    window.location.href = url;
  };

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ WebkitOverflowScrolling: 'touch' }}>
      {/* Background Image with loading blur */}
      <div
        className={`
          absolute inset-0 bg-cover bg-center bg-no-repeat 
          hover:scale-105 transition-all duration-[10s] ease-out
          ${imageLoaded ? 'filter-none' : 'filter blur-md'}
        `}
        style={{
          backgroundImage: imageLoaded ? `url(${backgroundImage})` : 'none',
          backgroundColor: imageLoaded ? 'transparent' : '#1f1f23'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/40 to-black/50" />
        <div className="absolute inset-0 bg-gradient-to-r from-purple-900/20 via-transparent to-pink-900/20" />
      </div>

      {/* Loading placeholder */}
      {!imageLoaded && (
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900 via-pink-900 to-rose-900 animate-pulse">
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/40 to-black/50" />
        </div>
      )}

      {/* Enhanced Sparkle Effect */}
      <SparkleEffect />

      {/* Content with optimized mobile spacing */}
      <div className={`
        relative z-20 min-h-screen flex flex-col items-center justify-center 
        px-4 sm:px-6 py-8 sm:py-0
        transform transition-all duration-1000 ease-out
        ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}
      `}>
        <div className="text-center max-w-2xl mx-auto space-y-6 sm:space-y-8">
          {/* Enhanced Title */}
          <h1 className={`
            text-5xl sm:text-6xl md:text-8xl font-bold text-white mb-4 sm:mb-8 tracking-wide
            transform transition-all duration-1200 delay-300 ease-out
            ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'}
          `}>
            <span className="bg-gradient-to-r from-pink-400 via-purple-400 to-pink-300 bg-clip-text text-transparent 
                           drop-shadow-2xl animate-gradient-shift hover:animate-smooth-glow 
                           transition-all duration-500 ease-in-out cursor-default">
              GENTLEMELT
            </span>
          </h1>

          {/* Enhanced Description */}
          <div className={`
            text-lg sm:text-xl md:text-2xl text-white mb-8 sm:mb-12 md:mb-16 leading-relaxed font-medium
            transform transition-all duration-1000 delay-600 ease-out
            ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}
          `}>
            <p className="mb-2 sm:mb-3 drop-shadow-lg hover:scale-105 transition-transform duration-300 cursor-default">
              🔥 Découvre mes côtés les plus interdits...
            </p>
            <p className="mb-2 sm:mb-3 drop-shadow-lg hover:scale-105 transition-transform duration-300 cursor-default">
              Tout ce que je garde uniquement pour toi 😈
            </p>
            <p className="drop-shadow-lg hover:scale-105 transition-transform duration-300 cursor-default">
              Rejoins-moi vite, je t'attends 💋
            </p>
          </div>

          {/* CTA Cards */}
          <div className={`
            flex flex-col gap-4 sm:gap-5 justify-center items-center mb-6 sm:mb-8
            transform transition-all duration-1000 delay-900 ease-out
            ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}
          `}>
            {/* Fanvue Card */}
            <div className={`
              w-full max-w-xs sm:max-w-sm md:max-w-md
              transform transition-all duration-600 delay-1100 ease-out
              ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}
            `}>
              <button
                onClick={handleFanvueClick}
                className="w-full bg-slate-800/90 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-5 md:p-6
                         shadow-2xl hover:shadow-3xl hover:bg-slate-700/90
                         transform hover:scale-[1.02] active:scale-[0.98]
                         transition-all duration-200 ease-out
                         border border-slate-700/50 relative overflow-hidden
                         will-change-transform"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                {/* OFFRE LIMITÉE Badge - Top Right with Blinking Animation */}
                <div className="absolute top-2 right-2 sm:top-3 sm:right-3">
                  <span className="bg-rose-600 text-white text-[10px] sm:text-xs font-bold px-2 sm:px-3 py-0.5 sm:py-1 rounded-full shadow-lg animate-blink-shine">
                    OFFRE LIMITÉE
                  </span>
                </div>

                {/* Single Line Layout: Icon + Title + Description */}
                <div className="flex items-center gap-3 sm:gap-4 pr-20 sm:pr-24 mb-3 sm:mb-4">
                  {/* Fanvue Logo */}
                  <div className="flex-shrink-0">
                    <img
                      src="/images/fanvue-logo.png"
                      alt="Fanvue"
                      className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-xl sm:rounded-2xl"
                    />
                  </div>

                  {/* Title and Description */}
                  <div className="flex-1 text-left min-w-0">
                    <h3 className="text-white text-lg sm:text-xl md:text-2xl font-bold mb-0.5">
                      Fanvue
                    </h3>
                    <p className="text-gray-300 text-xs sm:text-sm md:text-base whitespace-nowrap">
                      Contenus exclusifs ultra hot 🔥
                    </p>
                  </div>
                </div>

                {/* Promotional Banner */}
                <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-lg p-2.5 sm:p-3 shadow-lg">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="bg-white text-green-700 text-xs sm:text-sm font-black px-2 py-0.5 rounded-md">
                        -50%
                      </span>
                      <span className="text-white text-xs sm:text-sm font-semibold">
                        sur l'abonnement
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 sm:gap-2">
                      <span className="text-white/70 text-xs sm:text-sm line-through">
                        17,02€
                      </span>
                      <span className="text-white text-sm sm:text-base font-bold">
                        8,51€
                      </span>
                    </div>
                  </div>
                </div>
              </button>
            </div>

            {/* Telegram Privé Card */}
            <div className={`
              w-full max-w-xs sm:max-w-sm md:max-w-md
              transform transition-all duration-600 delay-1300 ease-out
              ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}
            `}>
              <button
                onClick={handleTelegramClick}
                className="w-full bg-slate-800/90 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-5 md:p-6
                         shadow-2xl hover:shadow-3xl hover:bg-slate-700/90
                         transform hover:scale-[1.02] active:scale-[0.98]
                         transition-all duration-200 ease-out
                         border border-slate-700/50
                         will-change-transform"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <div className="flex items-center gap-3 sm:gap-4">
                  {/* Telegram Icon */}
                  <div className="bg-gradient-to-br from-blue-400 to-cyan-500 rounded-xl sm:rounded-2xl p-3 sm:p-4 flex-shrink-0">
                    <svg className="w-7 h-7 sm:w-8 sm:h-8 text-white" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z" />
                    </svg>
                  </div>

                  {/* Content */}
                  <div className="flex-1 text-left min-w-0">
                    <h3 className="text-white text-lg sm:text-xl md:text-2xl font-bold mb-0.5 sm:mb-1">
                      Telegram Privé
                    </h3>
                    <p className="text-gray-300 text-xs sm:text-sm md:text-base">
                      Rejoignez la communauté exclusive
                    </p>
                  </div>
                </div>
              </button>
            </div>
          </div>

          {/* Enhanced Footer */}
          <div className={`
            text-white/80 text-xs sm:text-sm
            transform transition-all duration-1000 delay-1500 ease-out
            ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}
          `}>
            <p className="drop-shadow-md hover:text-white transition-colors duration-300 cursor-default">
              Clique sur les boutons pour me rejoindre ✨
            </p>
          </div>
        </div>
      </div>

      {/* Enhanced decorative elements */}
      <div className="absolute bottom-0 left-0 w-full h-32 sm:h-40 bg-gradient-to-t from-black/60 via-black/30 to-transparent pointer-events-none" />

      {/* Floating orbs for ambient effect */}
      <div className={`
        absolute top-1/4 left-1/4 w-24 sm:w-32 h-24 sm:h-32 bg-pink-500/10 rounded-full blur-3xl animate-pulse
        transition-opacity duration-1000 delay-500
        ${isVisible ? 'opacity-100' : 'opacity-0'}
      `} />
      <div className={`
        absolute top-1/3 right-1/4 w-20 sm:w-24 h-20 sm:h-24 bg-purple-500/10 rounded-full blur-3xl animate-pulse delay-1000
        transition-opacity duration-1000 delay-800
        ${isVisible ? 'opacity-100' : 'opacity-0'}
      `} />
      <div className={`
        absolute bottom-1/3 left-1/3 w-16 sm:w-20 h-16 sm:h-20 bg-rose-500/10 rounded-full blur-3xl animate-pulse delay-2000
        transition-opacity duration-1000 delay-1100
        ${isVisible ? 'opacity-100' : 'opacity-0'}
      `} />
    </div>
  );
};

export default LandingPage;